import { config } from '@iheartjane/config';
import { useRuntimeConfig } from '@iheartjane/runtime-config';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';

import { useInterval, useIsOnline } from '@jane/shared/hooks';
import { getIsDocumentVisible } from '@jane/shared/util';

import { useSource } from './useSource';

const DEFAULT_POLLING_INTERVAL_SECONDS = 60 * 5;

interface Props {
  currentVersion?: string;
  pollingIntervalSeconds?: number;
}

interface VersionData {
  buildVersion: string;
}

export const useForceClientRefresh = (
  {
    currentVersion = config.buildVersion,
    pollingIntervalSeconds = DEFAULT_POLLING_INTERVAL_SECONDS,
  }: Props = {
    currentVersion: config.buildVersion,
    pollingIntervalSeconds: DEFAULT_POLLING_INTERVAL_SECONDS,
  }
) => {
  const location = useLocation();
  const [pullNewVersion, setPullNewVersion] = useState(false);
  const [newVersionAvailable, setNewVersionAvailable] = useState(false);
  const { isPartnerHosted } = useRuntimeConfig();
  const isOnline = useIsOnline();

  const { data, fetch } = useSource<VersionData>(
    {
      url: isPartnerHosted
        ? `${config.deployPublicPath}version.json`
        : '/version.json',
    },
    { trackErrors: false, manual: true, simple: true }
  );

  useInterval(
    () => {
      setPullNewVersion(true);
    },
    newVersionAvailable ? null : pollingIntervalSeconds * 1000
  );

  useEffect(() => {
    const documentVisible = getIsDocumentVisible();
    if (!documentVisible) {
      setPullNewVersion(false);
    }
    if (documentVisible && pullNewVersion && isOnline) {
      fetch();
      setPullNewVersion(false);
    }
  }, [pullNewVersion, isOnline]);

  useEffect(() => {
    if (!data) {
      return;
    }
    if (data.buildVersion !== currentVersion) {
      setNewVersionAvailable(data.buildVersion !== currentVersion);
    }
  }, [data, currentVersion]);

  useEffect(() => {
    if (newVersionAvailable && !location.pathname.includes('checkout')) {
      window.location.reload();
    }
  }, [newVersionAvailable, location]);
};
